import React, { useState, useEffect } from 'react';
// Common Components
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils
import { getPurchaseReturns } from '../../../api/PurchaseReturnsAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const PurchaseReturnsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Purchase Return');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);

    const [returnsList, setReturnsList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    // Navigation
    const handleAddClick = () => navigate('/franchisee_user/inventory/purchase-returns/create');
    const handleViewClick = (id) => navigate(`/franchisee_user/inventory/purchase-returns/view/${id}`);
    const handleEditClick = (id) => navigate(`/franchisee_user/inventory/purchase-returns/edit/${id}`);

    // Delete (Disabled for financial integrity)
    const handleDeleteClick = () => {
        Swal.fire('Action Not Allowed', 'Posted Purchase Returns cannot be deleted.', 'warning');
    };

    // Effects
    useEffect(() => {
         if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
         }
     }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
         document.title = 'Manage Purchase Returns';
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);

         getPurchaseReturns(filters, function (response) {
             setViewDataLoading(false);
             if (response.purchase_returns) {
                 let data = response.purchase_returns.data;
                 setReturnsList({
                     paginate: filters.paginate,
                     per_page: response.purchase_returns.per_page,
                     data: data,
                     current_page: response.purchase_returns.current_page,
                     tot_page: response.purchase_returns.last_page
                 });
             } else {
                  handleApiError(response);
                  setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Purchase Returns.' });
                  setShowToaster(true);
             }
         });
     }, [currentPage, searchTermCompleted, perPage]);

    const formatDate = (dateString) => {
        try { return format(parseISO(dateString), 'dd-MMM-yyyy'); } catch (e) { return dateString; }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Purchase Returns List'>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Purchase Returns</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white" >
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Returns</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> New Return (Debit Note)
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {returnsList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={returnsList.current_page} totalPages={returnsList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Purchase Returns found.</p> )}
                                    {(viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && returnsList.data.map((pr) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={pr.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-truck-return f-s-18"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{pr.party?.name || 'N/A'}</h6>
                                                                <small className="text-muted d-block">
                                                                    Note #: {pr.return_note_number} | {formatDate(pr.return_date)}
                                                                </small>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    {/* <li><a className="dropdown-item" onClick={() => handleViewClick(pr.id)}><i className="ti ti-eye text-info me-1" /> View Details</a></li> */}
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(pr.id)}><i className="ti ti-edit text-info me-1" /> Edit / View</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body py-2">
                                                         <div className="d-flex justify-content-between align-items-center">
                                                             <div>
                                                                  <p className="f-w-500 text-secondary mb-0">Debit Amount</p>
                                                                  <h6 className="f-w-600 mb-0">₹{parseFloat(pr.total_amount || 0).toFixed(2)}</h6>
                                                             </div>
                                                             {pr.purchase_invoice && (
                                                                 <div className='text-end'>
                                                                      <p className="f-w-500 text-secondary mb-0">Against Invoice</p>
                                                                      <span className="badge bg-light text-dark border">{pr.purchase_invoice.invoice_number}</span>
                                                                 </div>
                                                             )}
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Paginator repeated at bottom if needed */}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default PurchaseReturnsPage;